import type {
  NormalizationType,
  OCGCurveType,
  OCGTerritory,
} from './data-typing';

import { savitzkyGolaySmooth } from '@/src/store/calculators/OCGCalculatorStore';

export class BiomarkerOCGCurve {
  private biomarkerName: OCGCurveType;
  private territory: OCGTerritory;
  private normalization: NormalizationType = 'none';
  private data: (number | null)[];
  private sliceIndex: string;
  private interpolatedIndexes: number[] | null = null;
  private isCycleNorm: boolean = false;
  private cycleType: string | null = null;
  private startFrame: number | null = null;
  private endFrame: number | null = null;
  private esFrame: number | null = null;
  private esPhaseIndex: number | null = null;
  private edPhaseIndexes: number[] | null = null;

  private availOperations = [
    'min',
    'max',
    'median',
    'mean',
    // 'TTP',
    'AUC',
    // 'FWHM',
  ];

  private operations: Record<
    (typeof this.availOperations)[number],
    (arr: (number | null)[]) => number
  > = {
    min: (arr: (number | null)[]) => {
      const valid = arr.filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v),
      );
      return valid.length ? Math.min(...valid) : 0;
    },
    max: (arr: (number | null)[]) => {
      const valid = arr.filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v),
      );
      return valid.length ? Math.max(...valid) : 0;
    },
    median: (arr: (number | null)[]) => {
      const valid = arr.filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v),
      );
      if (!valid.length) return 0;
      const sorted = [...valid].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
    },
    mean: (arr: (number | null)[]) => {
      const valid = arr.filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v),
      );
      return valid.length
        ? valid.reduce((sum, num) => sum + num, 0) / valid.length
        : 0;
    },
    // // TTP: Returns the index of the maximum value (assumes uniform time spacing)
    // TTP: (arr: number[]) => arr.indexOf(Math.max(...arr)),
    // AUC: Trapezoidal integration (assuming unit spacing between indices)
    AUC: (arr: (number | null)[]) => {
      const valid = arr.filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v),
      );
      if (valid.length < 2) return valid.length ? valid[0] : 0;
      let auc = 0;
      for (let i = 1; i < valid.length; i++) {
        auc += (valid[i] + valid[i - 1]) / 2;
      }
      return auc;
    },
    // FWHM: Calculates the width (in index units) where the value is at least half of the maximum
    FWHM: (arr: (number | null)[]) => {
      const valid = arr.filter(
        (v): v is number => v !== null && v !== undefined && Number.isFinite(v),
      );
      if (!valid.length) return 0;
      const maxVal = Math.max(...valid);
      const halfMax = maxVal / 2;
      let left = -1;
      let right = -1;
      for (let i = 0; i < valid.length; i++) {
        if (valid[i] >= halfMax) {
          left = i;
          break;
        }
      }
      for (let i = valid.length - 1; i >= 0; i--) {
        if (valid[i] >= halfMax) {
          right = i;
          break;
        }
      }
      if (left === -1 || right === -1) return 0;
      return right - left;
    },
  };

  constructor(
    biomarkerName: OCGCurveType,
    territory: OCGTerritory,
    normalization: NormalizationType,
    data: (number | null)[],
    sliceIndex: string,
    interpolatedIndexes: number[] | null = null,
    isCycleNormalized: boolean | null = null,
    cycleType: string | null = null,
    startFrame: number | null = null,
    endFrame: number | null = null,
    esFrame: number | null = null,
    esPhaseIndex: number | null = null,
    edPhaseIndexes: number[] | null = null,
  ) {
    this.biomarkerName = biomarkerName;
    this.territory = territory;
    this.normalization = normalization || 'none';
    this.data = data;
    this.interpolatedIndexes = interpolatedIndexes;
    this.sliceIndex = sliceIndex;
    this.isCycleNorm = Boolean(isCycleNormalized);
    this.cycleType = cycleType;
    this.startFrame = startFrame;
    this.endFrame = endFrame;
    this.esFrame = esFrame;
    this.esPhaseIndex = esPhaseIndex;
    this.edPhaseIndexes = edPhaseIndexes;
  }

  public getBiomarkerName(): OCGCurveType {
    return this.biomarkerName;
  }

  public getBiomarkerNameWithTerritory(): string {
    return `${this.biomarkerName} (${this.territory})`;
  }

  public getTerritory(): OCGTerritory {
    return this.territory;
  }

  public getData(): (number | null)[] {
    return this.data;
  }

  public getSmoothData(smoothType = 'svGol'): (number | null)[] {
    if (smoothType !== 'svGol') {
      return [...this.data];
    }

    return this.getSavitzkyGolayData();
  }

  private getSavitzkyGolayData(): (number | null)[] {
    if (!this.data || this.data.length === 0) return [];
    const hasNull = this.data.some(
      v => v === null || v === undefined || !Number.isFinite(v),
    );
    if (!hasNull) {
      return savitzkyGolaySmooth(this.data as number[], 5, 2);
    }
    // Interpolate missing values for smooth filter
    const cleaned = [...this.data] as number[];
    let lastValid = 0;
    for (let i = 0; i < cleaned.length; i++) {
      if (
        cleaned[i] !== null
        && cleaned[i] !== undefined
        && Number.isFinite(cleaned[i])
      ) {
        lastValid = cleaned[i];
        break;
      }
    }
    for (let i = 0; i < cleaned.length; i++) {
      if (
        cleaned[i] === null
        || cleaned[i] === undefined
        || !Number.isFinite(cleaned[i])
      ) {
        cleaned[i] = lastValid;
      }
      else {
        lastValid = cleaned[i];
      }
    }
    return savitzkyGolaySmooth(cleaned, 5, 2);
  }

  public getSliceIndex(): string {
    return this.sliceIndex;
  }

  public getInterpolatedIndexes(): number[] | null {
    return this.interpolatedIndexes;
  }

  public getNormalization(): NormalizationType {
    return this.normalization;
  }

  public isCycleNormalized(): boolean {
    return this.isCycleNorm;
  }

  public getCycleType(): string | null {
    return this.cycleType;
  }

  public getStartFrame(): number | null {
    return this.startFrame;
  }

  public getEndFrame(): number | null {
    return this.endFrame;
  }

  public getEsFrame(): number | null {
    return this.esFrame;
  }

  public getEsPhaseIndex(): number | null {
    return this.esPhaseIndex;
  }

  public getEdPhaseIndexes(): number[] | null {
    return this.edPhaseIndexes;
  }

  public mapFrameToPhase(frame: number): number | null {
    if (this.startFrame === null || this.endFrame === null || this.endFrame <= this.startFrame) {
      return null;
    }
    if (frame < this.startFrame || frame > this.endFrame) {
      return null;
    }
    const points = this.data.length;
    if (points <= 1) return 0;
    return ((frame - this.startFrame) / (this.endFrame - this.startFrame)) * (points - 1);
  }

  public mapPhaseToFrame(phase: number): number | null {
    if (this.startFrame === null || this.endFrame === null || this.endFrame <= this.startFrame) {
      return null;
    }
    const points = this.data.length;
    if (points <= 1) return this.startFrame;
    const clampedPhase = Math.max(0, Math.min(points - 1, phase));
    const frac = clampedPhase / (points - 1);
    return Math.round(this.startFrame + frac * (this.endFrame - this.startFrame));
  }

  public getOperation(operation: string) {
    if (!this.availOperations.includes(operation)) {
      throw new Error(
        `Invalid operation. Available operations are: ${this.availOperations.join(
          ', ',
        )}`,
      );
    }
    return this.operations[operation](this.data);
  }

  public getNormalizedData(): (number | null)[] {
    const valid = this.data.filter(
      (v): v is number => v !== null && v !== undefined && Number.isFinite(v),
    );
    if (!valid.length) return [...this.data];
    const min = Math.min(...valid);
    const max = Math.max(...valid);
    const diff = max - min || 1;
    return this.data.map(d =>
      d === null || d === undefined || !Number.isFinite(d)
        ? null
        : (d - min) / diff,
    );
  }

  public getStructuredDataForTable() {
    /*
    Template:
     {
      title: this.getBiomarkerNameWithTerritory(),
      headers: [Min, Max],
      rows: [
        [5,6],
      ],
    },
    */
    const structuredData = {
      title: this.getBiomarkerNameWithTerritory(),
      headers: this.availOperations.map(
        operation => operation.charAt(0).toUpperCase() + operation.slice(1),
      ),
      rows: [
        this.availOperations.map(operation =>
          Number(this.getOperation(operation).toFixed(2)),
        ),
      ],
    };
    return structuredData;
  }
}
